import { useEffect, useState } from 'react'
import {
  defaultBrandSettings,
  getRootVarsByBrandColors,
  isSioHost,
  LOCAL_STORAGE_BRAND_DATA_ID,
} from '@systemeio/util'
import { BrandSettingsInterface } from '@systemeio/types'

const combineWithDefault = (brand?: BrandSettingsInterface): BrandSettingsInterface => ({
  name: brand?.name ?? defaultBrandSettings.name,
  colors: brand?.colors ?? defaultBrandSettings.colors,
  logo: brand?.logo ?? defaultBrandSettings.logo,
  favicon: brand?.favicon ?? defaultBrandSettings.favicon,
  dashboardHostname: brand?.dashboardHostname ?? defaultBrandSettings.dashboardHostname,
})

const fetchBrand = async (): Promise<BrandSettingsInterface | undefined> => {
  try {
    const brand = await fetch('/api/brand-settings')
    const brandData = await brand.json()

    return brandData
  } catch {
    return
  }
}

export const getBrand = async (fresh: boolean = false): Promise<BrandSettingsInterface> => {
  if (isSioHost(window.location.hostname)) {
    return defaultBrandSettings
  }

  const getFromApi = async () => {
    const brandData = await fetchBrand()

    if (brandData) {
      localStorage.setItem(
        LOCAL_STORAGE_BRAND_DATA_ID,
        JSON.stringify(combineWithDefault(brandData)),
      )
    }

    return combineWithDefault(brandData)
  }

  if (fresh) {
    return await getFromApi()
  }

  const data = localStorage.getItem(LOCAL_STORAGE_BRAND_DATA_ID)

  if (data) {
    try {
      return JSON.parse(data)
    } catch {
      return await getFromApi()
    }
  } else {
    return await getFromApi()
  }
}

export const applyBrand = (brand: BrandSettingsInterface) => {
  const faviconLink = document.createElement('link')
  faviconLink.rel = 'icon'

  if (brand.favicon?.path) {
    faviconLink.href = brand.favicon.path
  }

  document.head.appendChild(faviconLink)

  if (!brand.colors) return

  const rootVars = getRootVarsByBrandColors(brand.colors)
  const styleElement = document.createElement('style')
  styleElement.innerHTML = `
      html[white-label-theme] {
        ${rootVars}
      }
    `
  document.head.appendChild(styleElement)
}

export const useBrand = () => {
  const [brand, setBrand] = useState<BrandSettingsInterface>()

  useEffect(() => {
    const pullBrand = async () => {
      const brandData = await getBrand()

      setBrand(brandData)
    }

    pullBrand()
  }, [])

  return { brand }
}
